/* eslint-disable sonarjs/no-ignored-exceptions */
/* eslint-disable sonarjs/post-message */
import { useEffect, useRef, useState } from "react";
import { usePlaidLink } from "react-plaid-link";
import { ScreeningFlowProps } from "./types";

// Events that Boom handles internally and should not be sent to the parent window
const ON_MESSAGE_BLACKLISTED_EVENTS = [
  "request_configuration",
  "identity_verification_handoff",
  "plaid_income_handoff"
];

export const ScreeningFlow = ({
  env,
  token,
  onMessage,
  height,
  width,
  iFrameStyle
}: ScreeningFlowProps) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [identityVerificationData, setIdentityVerificationData] = useState<{
    linkToken: string;
    applicationId: number;
    applicantId: number;
    verificationId: string;
    originalUrl: string;
  } | null>(null);
  const [incomeVerificationData, setIncomeVerificationData] = useState<{
    linkToken: string;
    applicationId: number;
    verificationId: string;
    selectedMethod?: string;
    verificationMethod?: any;
    originalUrl: string;
  } | null>(null);
  const [showPlaidLink, setShowPlaidLink] = useState(false);
  const [plaidLinkType, setPlaidLinkType] = useState<
    "identity" | "income" | null
  >(null);

  // Plaid Link configuration
  const { open: openPlaidLink, ready: plaidLinkReady } = usePlaidLink({
    token:
      identityVerificationData?.linkToken ||
      incomeVerificationData?.linkToken ||
      "",
    onSuccess: (publicToken, metadata) => {
      // Hide Plaid Link first
      setShowPlaidLink(false);

      // Determine which type of verification this is
      const isIdentityVerification =
        plaidLinkType === "identity" && identityVerificationData;
      const isIncomeVerification =
        plaidLinkType === "income" && incomeVerificationData;

      // Navigate back to the original page
      const originalUrl =
        identityVerificationData?.originalUrl ||
        incomeVerificationData?.originalUrl;
      if (originalUrl && iframeRef.current) iframeRef.current.src = originalUrl;

      // Wait for iframe to load, then send success message
      const sendSuccessMessage = () => {
        let successMessage;

        if (isIdentityVerification)
          successMessage = {
            type: "identity_verification_success",
            timestamp: Date.now(),
            linkSessionId: metadata.link_session_id,
            publicToken,
            metadata,
            applicationId: identityVerificationData.applicationId,
            applicantId: identityVerificationData.applicantId,
            verificationId: identityVerificationData.verificationId
          };
        else if (isIncomeVerification)
          successMessage = {
            type: "plaid_income_success",
            timestamp: Date.now(),
            linkSessionId: metadata.link_session_id,
            publicToken,
            metadata,
            applicationId: incomeVerificationData.applicationId,
            verificationId: incomeVerificationData.verificationId,
            selectedMethod: incomeVerificationData.selectedMethod,
            verificationMethod: incomeVerificationData.verificationMethod
          };
        else return;

        // Send success message to the applicant iframe
        if (iframeRef.current?.contentWindow)
          try {
            iframeRef.current.contentWindow.postMessage(successMessage, "*");
          } catch (error) {
            //
          }

        // Also send to parent window in case of nested iframes
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        if (window.parent && window.parent !== window)
          try {
            window.parent.postMessage(successMessage, "*");
          } catch (error) {
            //
          }
      };

      // Send immediately and also after a short delay to ensure iframe is loaded
      sendSuccessMessage();
      setTimeout(sendSuccessMessage, 500);
      setTimeout(sendSuccessMessage, 1000);

      // Clear the verification data after a delay
      setTimeout(() => {
        setIdentityVerificationData(null);
        setIncomeVerificationData(null);
        setPlaidLinkType(null);
      }, 2000);
    },
    onExit: (error, metadata) => {
      // Hide Plaid Link first
      setShowPlaidLink(false);

      // Navigate back to the original page the customer left off
      if (identityVerificationData?.originalUrl && iframeRef.current)
        iframeRef.current.src = identityVerificationData.originalUrl;

      // Wait for iframe to load, then send exit message
      const sendExitMessage = () => {
        const exitMessage = {
          type: "identity_verification_exit",
          timestamp: Date.now(),
          error: error || null,
          metadata,
          applicationId: identityVerificationData?.applicationId,
          applicantId: identityVerificationData?.applicantId,
          verificationId: identityVerificationData?.verificationId
        };

        // Send exit message to the applicant iframe
        if (iframeRef.current?.contentWindow)
          try {
            iframeRef.current.contentWindow.postMessage(exitMessage, "*");
          } catch (error) {
            //
          }

        // Also send to parent window in case of nested iframes
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        if (window.parent && window.parent !== window)
          try {
            window.parent.postMessage(exitMessage, "*");
          } catch (error) {
            //
          }
      };

      // Send immediately and also after a short delay to ensure iframe is loaded
      sendExitMessage();

      // Clear the identity verification data after a delay
      setTimeout(() => {
        setIdentityVerificationData(null);
      }, 2000);
    },
    onEvent: (_eventName, _metadata) => {
      //
    }
  });

  // Auto-open Plaid Link when ready and showing
  useEffect(() => {
    if (
      showPlaidLink &&
      plaidLinkReady &&
      (identityVerificationData?.linkToken || incomeVerificationData?.linkToken)
    )
      openPlaidLink();
  }, [
    showPlaidLink,
    plaidLinkReady,
    identityVerificationData?.linkToken,
    incomeVerificationData?.linkToken,
    openPlaidLink
  ]);

  // Determine the URL based on environment
  const getIframeUrl = (environment: string): string => {
    const temp = "/applications";
    const sdkParam = `?is_sdk=true&auth_token=${token}`;

    switch (environment) {
      case "sandbox":
        return "https://screen.sandbox.boompay.app" + temp + sdkParam;
      case "qa":
        return "https://screen.staging.boompay.app" + temp + sdkParam;
      case "prod":
        return "https://screen.boompay.app" + temp + sdkParam;
      default:
        return "http://localhost:3000" + temp + sdkParam;
    }
  };

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      // Only listen to messages from the expected origins
      const allowedOrigins = [
        "https://screen.sandbox.boompay.app",
        "https://screen.boompay.app",
        "https://screen.staging.boompay.app",
        "http://localhost:3000"
      ];

      if (!allowedOrigins.includes(event.origin)) return;

      if (
        onMessage &&
        !ON_MESSAGE_BLACKLISTED_EVENTS.includes(
          (event as MessageEvent).data?.type
        )
      )
        onMessage(event);
    };

    const handleConfigurationRequest = (event: CustomEvent | MessageEvent) => {
      // Handle request_configuration event
      if (
        event.type === "request_configuration" ||
        (event as MessageEvent).data?.type === "request_configuration"
      ) {
        // Send configuration response
        const configResponse = {
          type: "configuration_response",
          status: "connected",
          timestamp: Date.now(),
          environment: env,
          connection_confirmed: true
        };

        // Post message to the requesting window
        if ("source" in event && event.source)
          (event.source as Window).postMessage(configResponse, "*");
        else {
          // Also broadcast to parent and current window
          // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
          if (window.parent && window.parent !== window)
            window.parent.postMessage(configResponse, "*");

          window.postMessage(configResponse, "*");
        }
      }
    };

    const handleIdentityVerificationHandoff = (event: MessageEvent) => {
      // Handle identity_verification_handoff event
      if (event.data?.type === "identity_verification_handoff") {
        // Store the current iframe URL to return to after Plaid flow
        const currentUrl = iframeRef.current?.src || iframeUrl;

        // Set the identity verification data
        setIdentityVerificationData({
          linkToken: event.data.linkToken,
          applicationId: event.data.applicationId,
          applicantId: event.data.applicantId,
          verificationId: event.data.verificationId,
          originalUrl: currentUrl
        });

        // Set plaid link type and show Plaid Link
        setPlaidLinkType("identity");
        setShowPlaidLink(true);
      }
    };

    const handlePlaidIncomeHandoff = (event: MessageEvent) => {
      // Handle plaid_income_handoff event
      if (event.data?.type === "plaid_income_handoff") {
        // Store the current iframe URL to return to after Plaid flow
        const currentUrl = iframeRef.current?.src || iframeUrl;

        // Set the income verification data
        setIncomeVerificationData({
          linkToken: event.data.linkToken,
          applicationId: event.data.applicationId,
          verificationId: event.data.verificationId,
          selectedMethod: event.data.selectedMethod,
          verificationMethod: event.data.verificationMethod,
          originalUrl: currentUrl
        });

        // Set plaid link type and show Plaid Link
        setPlaidLinkType("income");
        setShowPlaidLink(true);
      }
    };

    window.addEventListener("message", handleMessage);
    window.addEventListener(
      "request_configuration",
      handleConfigurationRequest as EventListener
    );
    window.addEventListener("message", handleConfigurationRequest);
    window.addEventListener("message", handleIdentityVerificationHandoff);
    window.addEventListener("message", handlePlaidIncomeHandoff);

    return () => {
      window.removeEventListener("message", handleMessage);
      window.removeEventListener(
        "request_configuration",
        handleConfigurationRequest as EventListener
      );
      window.removeEventListener("message", handleConfigurationRequest);
      window.removeEventListener("message", handleIdentityVerificationHandoff);
      window.removeEventListener("message", handlePlaidIncomeHandoff);
    };
  }, [onMessage, env]);

  const iframeUrl = getIframeUrl(env);

  return (
    <iframe
      ref={iframeRef}
      src={iframeUrl}
      width={width || "100%"}
      height={height || "100%"}
      style={
        iFrameStyle || {
          border: "none",
          minHeight: "600px"
        }
      }
      title="Screening Application"
      sandbox="allow-scripts allow-same-origin allow-forms"
    />
  );
};
